import { api } from 'api';
import { preventKeyDownEnter } from 'components/common/func';
import {
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
  RESET_BACKDROP_STORE,
} from 'constants/common';
import { addressFinderTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useFormInput from 'hooks/form/useFormInput';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { SET_SHOW_BACKDROP } from 'store/action';
import styled from 'styled-components';
import ArrowDownIcon from '../../../../common/Icons/ArrowDownIcon';

const FormInputAddressFinderValidation = ({
  field,
  information,
  setInformation,
  validation,
  setValidation,
  addressSuggestion,
  setAddressSuggestion,
  setAddressFinder,
  checkFormInputValidation,
}) => {
  const { t } = useLang();
  const { state, dispatch } = useStore();

  const {
    inputRef,
    resetFormInput,
    setValidationFormInput,
    getValueTrim,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  // Last typed text that failed addressFinderTextValid. The box is only flagged while
  // it still holds exactly that text, so a selected suggestion (written into
  // the DOM without an onChange) is never validated.
  const [invalidText, setInvalidText] = useState(null);
  const invalidTextRef = useRef(null);

  const isTypedTextInvalid = () =>
    invalidTextRef.current !== null &&
    invalidTextRef.current === inputRef.current?.value;
  const isSearchInvalid =
    invalidText !== null && invalidText === inputRef.current?.value;

  const updateInvalidText = (value) => {
    invalidTextRef.current = value;
    setInvalidText(value);
  };

  // A queued or in-flight lookup is dropped when its typed text is invalid or
  // the box has since been cleared (the debounce has no cancel).
  const shouldDropLookup = () =>
    isTypedTextInvalid() || !(inputRef.current?.value ?? '').trim();

  const fetchLookupAddressSuggestion = async (value) => {
    try {
      const { data } = await api.lookupAddresses(
        value,
        'd6fe8ff05352dfff3c3a3367d7140364',
      );

      if (shouldDropLookup()) return;

      if (data?.payload) {
        setAddressSuggestion(data.payload);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };
  const checkLookupAddressSuggestion = async (value) => {
    if (!value) return;
    if (shouldDropLookup()) return;

    await fetchLookupAddressSuggestion(value);

    if (shouldDropLookup()) return;

    dispatch({
      type: SET_SHOW_BACKDROP,
      payload: {
        isShow: true,
        current: field.name,
      },
    });
  };
  const { debounce: handleLookupAddressSuggestion } = useDebounceCallback(
    (value) => {
      checkLookupAddressSuggestion(value);
    },
    DEBOUNCE_SEC,
  );

  const onReset = () => {
    document.getElementsByName(field.name)[0].value = '';
    resetFormInput({
      information,
      setInformation,
      validation,
      setValidation,
    });
    setAddressSuggestion(null);
    setAddressFinder(null);
  };

  const handleLookupFromInput = (e) => {
    const { value } = e.target;
    const valueTrim = getValueTrim(value);

    if (!valueTrim) {
      updateInvalidText(null);
      onReset();
      return;
    }

    handleLookupAddressSuggestion(valueTrim);
  };

  const handleOnChange = (e) => {
    const { value } = e.target;

    if (!addressFinderTextValid(value)) {
      updateInvalidText(value);
      setAddressSuggestion(null);
      if (state?.backdrop?.current === field.name) {
        dispatch({
          type: SET_SHOW_BACKDROP,
          payload: { isShow: false, current: RESET_BACKDROP_STORE.current },
        });
      }
      return;
    }

    updateInvalidText(null);
    handleLookupFromInput(e);
  };

  const handleOnClick = () => {
    setValidationFormInput({
      validation,
      setValidation,
      isReset: true,
    });

    setAddressSuggestion(null);

    dispatch({
      type: SET_SHOW_BACKDROP,
      payload: {
        isShow: false,
        current: RESET_BACKDROP_STORE.current,
        onClick: () => {
          setValidationFormInput({
            validation,
            setValidation,
            checkFormInputValidation: () =>
              checkFormInputValidation('', field, validation),
          });
        },
      },
    });
  };

  const handleOnBlur = (e) => {
    if (isTypedTextInvalid()) return;
    handleLookupFromInput(e);
  };

  const renderArrowDownIcon = () => {
    if (isEmpty(addressSuggestion)) return null;
    return (
      <ArrowDownIcon
        color="var(--ds-c-grey-dark)"
        size={{ width: 12, height: 12 }}
        styles={{
          position: 'absolute',
          top: '35px',
          right: '16px',
          transform: 'rotate(180deg)',
          cursor: 'pointer',
        }}
        onClick={handleOnClick}
      />
    );
  };

  return (
    <FormGroupStyled onKeyDown={preventKeyDownEnter}>
      <FormLabel />
      <FormControl
        ref={inputRef}
        type={field.type}
        name={field.name}
        defaultValue={information[field.name]}
        placeholder={field.placeholder ? t(field.placeholder) : ''}
        isInvalid={validation[field.name] || isSearchInvalid}
        onChange={handleOnChange}
        onClick={handleOnClick}
        onBlur={handleOnBlur}
      />
      <FormControlFeedback
        value={
          isSearchInvalid
            ? t('form_msg_search_validation')
            : t(validation[field.name])
        }
      />
      {renderArrowDownIcon()}
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  position: relative;
  margin-bottom: 32px;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 42px;
  }
`;

const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;

  color: var(--ds-c-grey-dark);
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 12px;
  padding-inline: 10px;
  background: var(--ds-c-white);

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    text-transform: none;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default FormInputAddressFinderValidation;
